import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

export default function Modal({ 
  isOpen, 
  onClose, 
  title, 
  subtitle, 
  children, 
  footer,
  icon: Icon,
  maxWidth = 'max-w-2xl',
  className
}) {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
      style={{
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)'
      }}
    >
      <div
        className={cn(
          "w-full bg-white border rounded-t-lg sm:rounded-lg p-0 overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex flex-col animate-in zoom-in-95 scale-100 duration-200",
          maxWidth,
          className
        )}
        style={{
          backgroundColor: 'var(--color-brand-card)',
          borderColor: 'var(--color-brand-border)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          maxHeight: '90vh'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="p-4 sm:p-6 border-b relative shrink-0"
          style={{
            borderBottomColor: 'var(--color-brand-border)',
            backgroundColor: 'var(--color-brand-hover)'
          }}
        >
           <button
             className="absolute top-4 right-4 md:top-6 md:right-6 transition-all duration-200 hover:scale-110"
             onClick={onClose}
             aria-label="Close modal"
             style={{ color: 'var(--color-text-dim)' }}
           >
             <X size={20} />
           </button>
           <div className="flex items-center gap-4">
              {Icon && (
                <div
                  className="w-12 h-12 md:w-14 md:h-14 rounded-[4px] border flex items-center justify-center shadow-sm"
                  style={{
                    backgroundColor: 'rgba(0, 112, 210, 0.08)',
                    borderColor: 'rgba(0, 112, 210, 0.15)',
                    color: 'var(--color-brand-primary)'
                  }}
                >
                   <Icon size={24} strokeWidth={1.5} />
                </div>
              )}
              <div>
                 <h2
                   className="text-lg md:text-xl font-semibold tracking-tight"
                   style={{ color: 'var(--color-text-main)' }}
                 >
                   {title}
                 </h2>
                 {subtitle && (
                   <p
                     className="text-[11px] md:text-[12px] font-medium uppercase tracking-wider mt-1"
                     style={{ color: 'var(--color-text-muted)' }}
                   >
                     {subtitle}
                   </p>
                 )}
              </div>
           </div>
        </div>

        {/* Content */}
        <div
          className="p-4 sm:p-6 flex-1 overflow-y-auto"
          style={{
            backgroundColor: 'var(--color-brand-card)',
            color: 'var(--color-text-main)',
            fontFamily: 'var(--font-family-sans)'
          }}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            className="p-3 sm:p-4 border-t modal-footer shrink-0"
            style={{
              borderTopColor: 'var(--color-brand-border)',
              backgroundColor: 'var(--color-brand-hover)',
              display: 'flex',
              gap: "0.5rem",
              justifyContent: 'flex-end'
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export { Modal };
